import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
});

// Interceptor de solicitud: agrega el token si existe
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helpers
function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('isLogged');
}

function redirectToLogin() {
  // Evitar redirect si ya estamos en signin para no loop
  if (window.location.pathname !== '/signin' && window.location.pathname !== '/') {
    window.location.href = '/signin';
  } else if (!window.location.pathname.includes('signin')) {
    window.location.href = '/signin';
  }
}

function getApiErrorMessage(err) {
  if (err?.response?.data?.message) return err.response.data.message;
  // compatibilidad: backend ya no garantiza detail/error pero lo respetamos si existe
  if (err?.response?.data?.detail) return err.response.data.detail;
  if (err?.response?.data?.error) return err.response.data.error;
  if (typeof err?.response?.data === 'string') return err.response.data;
  return err?.message || 'Error inesperado';
}

// Refresh Token automático al expirar el accessToken
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // No intentar refresh si la request original es el propio endpoint de refresh
    const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh-token');

    // 429 global: mostrar mensaje entendible, no reintentar
    if (status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'] || error.response?.headers?.['Retry-After'];
      let msg = 'Demasiados intentos. Intente nuevamente en unos minutos.';
      if (retryAfter) {
        const secs = parseInt(retryAfter, 10);
        if (!isNaN(secs) && secs > 0) {
          if (secs < 60) msg = `Demasiados intentos. Intente nuevamente en ${secs} segundos.`;
          else msg = `Demasiados intentos. Intente nuevamente en ${Math.ceil(secs / 60)} minutos.`;
        }
      }
      // Prioriza message del backend si es más específico y no es genérico
      const backendMsg = error.response?.data?.message;
      if (backendMsg && backendMsg.toLowerCase().includes('demasiados')) {
        msg = backendMsg;
      }
      // Normaliza mensaje para consumidores (Signin.vue usa error.response.data.message)
      if (error.response?.data) {
        error.response.data.message = backendMsg || msg;
      } else {
        error.response = { data: { message: msg }, status: 429, headers: error.response?.headers || {} };
      }
      return Promise.reject(error);
    }

    // Si refresh-token devuelve 401/403 => sesión inválida, limpiar y redirigir
    if (isRefreshRequest && (status === 401 || status === 403)) {
      clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    // 403 normal (permisos) => NO cerrar sesión, solo propagar
    // Un EMPLEADO sin permiso de admin debe seguir logueado
    if (status === 403 && !isRefreshRequest) {
      if (error.response?.data && !error.response.data.message) {
        error.response.data.message = getApiErrorMessage(error);
      }
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      !originalRequest._retry &&
      !isRefreshRequest &&
      localStorage.getItem('refreshToken')
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const res = await axios.post(`${API_URL}/auth/refresh-token`, {
          refreshToken,
        });

        const newToken = res.data.token;
        // Rotación: backend devuelve nuevo refreshToken
        const newRefreshToken = res.data.refreshToken || res.data.refresh_token;

        if (!newToken) throw new Error('Respuesta de refresh sin token');

        localStorage.setItem('token', newToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;

        processQueue(null, newToken);

        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        clearSession();
        redirectToLogin();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // 401 sin refreshToken => sesión inconsistente, limpiar y redirigir (FE-005)
    // Solo cuando: 401, no es refresh, no es login, no hay refreshToken, no está en proceso de refresh
    if (status === 401 && !isRefreshRequest) {
      const isLoginRequest = originalRequest?.url?.includes('/auth/login');
      const hasRefreshToken = !!localStorage.getItem('refreshToken');
      if (!isLoginRequest && !hasRefreshToken && !isRefreshing) {
        // Evitar limpiar si nunca hubo sesión (request público 401 sin token previo)
        // Pero si hubo intento con token previo, isLogged/token existió; limpiamos para evitar estado huérfano
        // La regla exige limpiar cuando 401 sin refreshToken y era request protegida
        const hadSession = localStorage.getItem('token') || localStorage.getItem('user') || localStorage.getItem('isLogged') === 'true';
        if (hadSession) {
          clearSession();
          redirectToLogin();
        }
      }
    }

    if (!error.response || !error.response.data) {
      error.response = {
        data: { message: getApiErrorMessage(error) || 'Error de red o respuesta inválida del servidor.' },
        status: status || 0,
        headers: error.response?.headers || {}
      };
    } else if (!error.response.data.message) {
      error.response.data.message = getApiErrorMessage(error);
    }

    return Promise.reject(error);
  }
);

// Función para login inicial
export const login = async (email, password) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });
    return response.data; // { token, refreshToken, user }
  } catch (error) {
    // Prioriza message, maneja 429 explícitamente
    const status = error.response?.status;
    if (status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'] || error.response?.headers?.['Retry-After'];
      let msg = error.response?.data?.message || 'Demasiados intentos. Intente nuevamente en unos minutos.';
      if (retryAfter && !msg.includes('segundos') && !msg.includes('minutos')) {
        const secs = parseInt(retryAfter, 10);
        if (!isNaN(secs) && secs > 0) {
          msg += ` (reintente en ${secs}s)`;
        }
      }
      throw msg;
    }
    throw error.response?.data?.message || error.response?.data?.detail || error.response?.data?.error || 'Error al iniciar sesión';
  }
};

export default api;
